import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import {
  type X57DisplayComponent,
  type X57VspAircraft,
} from "./x57VspBridge";
import type { X57SurfaceLines } from "./x57SurfaceModel";

interface LoomAircraftViewerProps {
  aircraft: X57VspAircraft;
  surfaceLines?: X57SurfaceLines | null;
  selectedSurfaceComponentId?: string | null;
}

function token(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    const renderable = child as THREE.Object3D & {
      geometry?: THREE.BufferGeometry;
      material?: THREE.Material | THREE.Material[];
    };
    renderable.geometry?.dispose();
    if (Array.isArray(renderable.material)) renderable.material.forEach((material) => material.dispose());
    else renderable.material?.dispose();
  });
}

function componentMesh(
  component: X57DisplayComponent,
  selectedSurfaceComponentId: string | null,
): THREE.Mesh {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(component.positions, 3));
  geometry.setIndex(new THREE.BufferAttribute(component.indices, 1));
  geometry.computeVertexNormals();
  const selectionActive = selectedSurfaceComponentId !== null;
  const selected = component.id === selectedSurfaceComponentId;
  const geometryOpacity = selectionActive ? (selected ? 0.18 : 0.08) : component.material.opacity;
  const material = new THREE.MeshStandardMaterial({
    color: selected ? token("--fc-primary", "#49a7e8") : component.material.color,
    metalness: 0.04,
    opacity: geometryOpacity,
    roughness: 0.72,
    side: THREE.DoubleSide,
    transparent: component.material.transparent || selectionActive,
    depthWrite: !selectionActive || selected,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = component.name;
  return mesh;
}

function addSurfaceConstruction(group: THREE.Group, lines: X57SurfaceLines): void {
  const parameterGeometry = new THREE.BufferGeometry();
  parameterGeometry.setAttribute("position", new THREE.BufferAttribute(lines.parameterLines, 3));
  const parameterLines = new THREE.LineSegments(
    parameterGeometry,
    new THREE.LineBasicMaterial({
      color: token("--fc-primary", "#49a7e8"),
      depthTest: false,
      opacity: 0.92,
      transparent: true,
    }),
  );
  parameterLines.renderOrder = 5;
  parameterLines.name = "VSP sampled parameter lines";
  group.add(parameterLines);

  const boundaryGeometry = new THREE.BufferGeometry();
  boundaryGeometry.setAttribute("position", new THREE.BufferAttribute(lines.boundaries, 3));
  const boundaries = new THREE.LineSegments(
    boundaryGeometry,
    new THREE.LineBasicMaterial({
      color: token("--fc-warning", "#e39c35"),
      depthTest: false,
      opacity: 0.98,
      transparent: true,
    }),
  );
  boundaries.renderOrder = 6;
  boundaries.name = "VSP surface boundaries";
  group.add(boundaries);
}

export function LoomAircraftViewer({
  aircraft,
  surfaceLines = null,
  selectedSurfaceComponentId = null,
}: LoomAircraftViewerProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const cameraFitKeyRef = useRef<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const runtimeRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    controls: OrbitControls;
    geometryGroup: THREE.Group;
    observer: ResizeObserver;
    raf: number;
  } | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    // React StrictMode recreates the renderer while preserving refs in DEV.
    // A fresh camera must always receive one fit even when the aircraft key is unchanged.
    cameraFitKeyRef.current = null;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    } catch (error) {
      setRenderError(error instanceof Error ? error.message : "WebGL renderer unavailable");
      return;
    }
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(token("--fc-surface-2", "#edf1f5"));
    const camera = new THREE.PerspectiveCamera(38, 1, 0.02, 5000);
    camera.up.set(0, 0, 1);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.domElement.setAttribute("aria-label", "Interactive imported surface assembly");
    renderer.domElement.setAttribute("data-testid", "parametric-wing-canvas");
    host.appendChild(renderer.domElement);

    const geometryGroup = new THREE.Group();
    scene.add(geometryGroup);
    scene.add(new THREE.HemisphereLight("#ffffff", "#708090", 2.2));
    const key = new THREE.DirectionalLight("#ffffff", 2.8);
    key.position.set(-20, -24, 45);
    scene.add(key);
    const grid = new THREE.GridHelper(80, 32, token("--fc-border-strong", "#8b96a3"), token("--fc-border", "#c8d0d8"));
    grid.rotation.x = Math.PI / 2;
    grid.position.z = 3;
    scene.add(grid);

    let raf = 0;
    const render = () => {
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(render);
    };
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.max(1, entry?.contentRect.width ?? host.clientWidth);
      const height = Math.max(1, entry?.contentRect.height ?? host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    });
    observer.observe(host);
    render();
    runtimeRef.current = { scene, camera, renderer, controls, geometryGroup, observer, raf };
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      controls.dispose();
      disposeObject(scene);
      renderer.dispose();
      renderer.forceContextLoss();
      if (renderer.domElement.parentElement === host) {
        host.removeChild(renderer.domElement);
      }
      runtimeRef.current = null;
    };
  }, []);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const { geometryGroup, camera, controls } = runtime;
    for (const child of [...geometryGroup.children]) {
      geometryGroup.remove(child);
      disposeObject(child);
    }
    for (const component of aircraft.components) {
      geometryGroup.add(componentMesh(component, selectedSurfaceComponentId));
    }
    if (surfaceLines) addSurfaceConstruction(geometryGroup, surfaceLines);

    const cameraFitKey = [
      aircraft.build.bundle_key,
      ...Object.entries(aircraft.parameterOverrides)
        .sort(([left], [right]) => left.localeCompare(right))
        .flatMap(([key, value]) => [key, value]),
    ].join(":");
    if (cameraFitKeyRef.current === cameraFitKey) return;
    cameraFitKeyRef.current = cameraFitKey;
    const bounds = new THREE.Box3().setFromObject(geometryGroup);
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z, 1);
    controls.target.copy(center);
    camera.position.set(center.x - radius * 0.9, center.y - radius * 1.05, center.z + radius * 0.48);
    camera.near = radius / 1000;
    camera.far = radius * 100;
    camera.updateProjectionMatrix();
    controls.update();
  }, [aircraft, selectedSurfaceComponentId, surfaceLines]);

  return (
    <div
      className="pg-viewer"
      data-component-count={aircraft.components.length}
      data-geometry-signature={aircraft.build.bundle_key}
      data-solved="false"
      data-testid="loom-aircraft-viewer"
      ref={hostRef}
    >
      {surfaceLines ? (
        <div
          className="pg-render-evidence"
          data-boundary-segment-count={surfaceLines.boundaryLineCount}
          data-parameter-segment-count={surfaceLines.parameterLineCount}
          data-testid="loom-surface-geometry"
        >{surfaceLines.label}</div>
      ) : null}
      {renderError ? (
        <div className="pg-render-error" role="status">
          <strong>3D workbench unavailable</strong>
          <span>{renderError}</span>
        </div>
      ) : null}
    </div>
  );
}
